<template>
  <Teleport to="body">
    <div
      v-if="open"
      ref="keyboardRef"
      class="mts-virtual-keyboard"
      :style="style"
      @touchmove.stop
      @touch.stop
    >
      <div ref="dragHandler" class="py-10px flex justify-center">
        <div class="w-46px h-8px rounded-4px bg-[#D9D9D9]"></div>
      </div>
      <div class="p-16px gap-10px flex pt-0">
        <div class="gap-10px w-260px flex flex-wrap">
          <div v-for="num in keyList" :key="num" class="itemKey" @click="handleKeyDown(num)">
            {{ num }}
          </div>
          <div v-if="!!symbol" class="itemKey" @click="handleKeyDown(symbol)">
            {{ symbol }}
          </div>
        </div>
        <div class="gap-10px flex flex-col">
          <div class="itemKey flex-basis-1/2" @click="handleDelete">
            <MIcon prefix="g" icon="key-delete" size="32" />
          </div>
          <div class="itemKey flex-basis-1/2 confirm-btn" @click="handleOk">
            {{ t('common.doneText') }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import { onClickOutside, useDraggable } from '@vueuse/core';
  import { ref } from 'vue';
  import type { SymbolKey } from '@mts-fe/hooks';
  import { adaptiveByVW } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { windowSize } from '@mts/utils';

  export interface VirtualKeyboardProps {
    symbol?: SymbolKey;
    position?: { x?: number; y?: number };
  }

  defineOptions({
    name: 'VirtualKeyboard',
    inheritAttrs: false,
  });
  const props = withDefaults(defineProps<VirtualKeyboardProps>(), { symbol: '.' });

  const keyList = new Array(9)
    .fill(0)
    .map((_, index) => (index + 1).toString())
    .concat(['00', '0']);

  const keyboardRef = ref<HTMLElement | null>(null);
  const dragHandler = ref<HTMLElement | null>(null);
  const { x, y } = props.position || {};
  const { style } = useDraggable(keyboardRef, {
    initialValue: {
      x: x ? adaptiveByVW(x) : windowSize.width - adaptiveByVW(410),
      y: y ? adaptiveByVW(y) : windowSize.height - adaptiveByVW(520),
    },
    draggingElement: dragHandler,
    preventDefault: true,
    stopPropagation: true,
    pointerTypes: ['touch'],
    onMove: (position) => {
      const boxWidth = keyboardRef.value?.clientWidth ?? 0;
      if (position.x > windowSize.width - boxWidth) {
        position.x = windowSize.width - boxWidth;
      }
      if (position.x < 0) {
        position.x = 0;
      }
      const boxHeight = keyboardRef.value?.clientHeight ?? 0;
      if (position.y > windowSize.height - boxHeight) {
        position.y = windowSize.height - boxHeight;
      }
      if (position.y < 0) {
        position.y = 0;
      }
    },
  });

  const emit = defineEmits(['keydown', 'delete', 'ok', 'close']);
  const handleKeyDown = (num: string | SymbolKey) => emit('keydown', num);

  const open = defineModel<boolean | number>('open', { default: false });
  const handleClose = () => {
    open.value = false;
    emit('close');
  };
  const handleDelete = () => emit('delete');
  const handleOk = () => {
    emit('ok');
    handleClose();
  };
  onClickOutside(keyboardRef, handleClose, { ignore: ['.mts-virtual-input'] });
</script>

<style lang="less">
  .mts-virtual-keyboard {
    position: fixed;
    z-index: 9999;
    border-radius: 16px;
    background-color: #f1f2f4f0;
    touch-action: none;
    box-shadow:
      0 3px 3px 0 #7276871f,
      0 3px 3px 0 #72768714,
      0 2px 14px 0 #7276870d;

    .itemKey {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 80px;
      transition: all 0.05s ease-in-out;
      border: 2px solid #fff;
      border-radius: @border-radius;
      background-color: #fff;
      font-size: 24px;
      line-height: 48px;

      &:active {
        border-color: @primary-color;
        background-color: #f3fffc;
        color: @primary-color;
      }

      &.confirm-btn {
        border-color: @primary-color;
        background-color: @primary-color;
        color: #fff;
        font-size: 20px;

        &:active {
          border-color: #228064;
          background-color: #228064;
        }
      }
    }
  }
</style>
